import { useSuspenseQuery } from '@tanstack/react-query';
import { Suspense, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { networksQueryOptions } from '#api/networks.queries.ts';
import { AuthLayout } from '#components/patterns/layouts/auth/AuthLayout.tsx';
import { useAuth } from '#global/context/auth/useAuth.tsx';
import { LoadingSpinner } from '#screens/loading/LoadingSpinner.tsx';
import { page, Titled } from '#util/title.ts';
import { GetStarted } from '../flow/GetStarted.tsx';

export function GetStartedPage() {
  const { user } = useAuth();
  // Hold the flow behind one settled networks fetch: with the revisit redirect decided before the
  // flow can mount, an in-flow create can never race the decision. An errored fetch throws to the
  // route's errorElement — the same treatment the GotStarted gate gives this failure on admin
  // routes — instead of guessing which arm the user belongs to.
  return (
    <Titled title={() => page('Get started')}>
      <AuthLayout headerText={<span className="truncate">{user?.email}</span>}>
        <Suspense fallback={<LoadingSpinner />}>
          <RevisitGate />
        </Suspense>
      </AuthLayout>
    </Titled>
  );
}

function RevisitGate() {
  const navigate = useNavigate();
  const { data } = useSuspenseQuery({
    ...networksQueryOptions,
    // The mount fetch is the only fetch the decision ever needs; settled data going stale is
    // harmless because the decision below is latched.
    staleTime: Number.POSITIVE_INFINITY,
  });
  // Latch the decision to the first settled fetch — the create's own invalidation refetch must
  // not re-run it and eject the user mid-flow.
  const [isRevisit] = useState(data.data.length > 0);

  useEffect(() => {
    if (isRevisit) {
      void navigate('/settings/networks', { replace: true });
    }
  }, [isRevisit, navigate]);

  return isRevisit ? null : <GetStarted />;
}
